import type { CombatContextResponse } from '../../../backend/shared/types/index';

export interface RecentCombatContext {
  campaignId?: string;
  characterId?: string;
  characterByCampaign: Record<string, string>;
}

const STORAGE_PREFIX = 'darkfeld_recent_combat_context_';
const UNASSIGNED_SCOPE = 'unassigned';

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`;
}

export function readRecentCombatContext(userId?: string): RecentCombatContext {
  if (!userId) return { characterByCampaign: {} };
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey(userId)) ?? '{}') as Partial<RecentCombatContext>;
    return {
      campaignId: typeof parsed.campaignId === 'string' ? parsed.campaignId : undefined,
      characterId: typeof parsed.characterId === 'string' ? parsed.characterId : undefined,
      characterByCampaign: parsed.characterByCampaign && typeof parsed.characterByCampaign === 'object'
        ? parsed.characterByCampaign
        : {},
    };
  } catch {
    return { characterByCampaign: {} };
  }
}

export function saveRecentCombatContext(userId: string | undefined, characterId: string, campaignId?: string) {
  if (!userId) return;
  try {
    const current = readRecentCombatContext(userId);
    localStorage.setItem(storageKey(userId), JSON.stringify({
      campaignId,
      characterId,
      characterByCampaign: {
        ...current.characterByCampaign,
        [campaignId ?? UNASSIGNED_SCOPE]: characterId,
      },
    } satisfies RecentCombatContext));
  } catch {
    // Combat navigation remains functional when browser storage is unavailable.
  }
}

export function selectCombatCharacter(context: CombatContextResponse, recent: RecentCombatContext) {
  if (recent.campaignId) {
    const campaign = context.campaigns.find((candidate) => candidate.campaignId === recent.campaignId);
    const rememberedId = recent.characterByCampaign[recent.campaignId] ?? recent.characterId;
    const remembered = campaign?.characters.find((character) => character.characterId === rememberedId);
    if (remembered) return remembered;
    if (campaign?.characters[0]) return campaign.characters[0];
  } else if (recent.characterId) {
    const remembered = context.unassignedCharacters.find((character) => character.characterId === recent.characterId);
    if (remembered) return remembered;
  }

  for (const campaign of context.campaigns) {
    if (campaign.characters[0]) return campaign.characters[0];
  }
  return context.unassignedCharacters[0];
}

export function rememberedCharacterForScope(recent: RecentCombatContext, campaignId?: string) {
  return recent.characterByCampaign[campaignId ?? UNASSIGNED_SCOPE];
}
